import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import type { DayOfWeek, ItemCategory, ItemImage } from "@/lib/types";
import {
  bindDevToolboxScenarioIdListener,
  bindDevToolboxScenarioListener,
} from "@/lib/devToolbox/bindScenarioListener";
import { isDevToolboxEnabled } from "@/lib/devToolbox/isDevToolboxEnabled";
import type { DevToolboxScenarioId } from "@/lib/devToolbox/types";
import {
  applySellPhotoCoachDismissState,
  getSellDevToolboxApplyResult,
  type DevImageValidationPreview,
} from "@/lib/devToolbox/sellerListingMocks";
import { primeSellPriceBandCache } from "@/lib/hooks/useSellPriceBand";
import type { SellListingMode } from "@/lib/listing/bulkListingTypes";
import type { SellListingFormData } from "@/lib/listing/sellListingFormTypes";
import type { SellListingFormErrors } from "@/lib/listing/sellListingValidation";

/** @deprecated Prefer `SellListingFormData` — kept for adapter exports. */
export type SellDevToolboxFormData = SellListingFormData;

export type SellDevToolboxContext = {
  lastScenarioRef: MutableRefObject<DevToolboxScenarioId | null>;
  setListingMode: (mode: SellListingMode) => void;
  setFormData: Dispatch<SetStateAction<SellListingFormData>>;
  setUploadedImages: Dispatch<SetStateAction<ItemImage[]>>;
  setPickupDays: Dispatch<SetStateAction<DayOfWeek[]>>;
  setErrors: Dispatch<SetStateAction<SellListingFormErrors>>;
  setDevSellInitialStep: Dispatch<SetStateAction<number>>;
  setDevSellBandErrorOverride: Dispatch<SetStateAction<string | null>>;
  setDevImageValidationPreview: Dispatch<
    SetStateAction<DevImageValidationPreview>
  >;
};

const isSellerScenario = (scenarioId: DevToolboxScenarioId | null) =>
  Boolean(scenarioId?.startsWith("seller-listing."));

export function applyDevToolboxScenario(
  scenarioId: DevToolboxScenarioId | null,
  context: SellDevToolboxContext,
): void {
  const {
    lastScenarioRef,
    setListingMode,
    setFormData,
    setUploadedImages,
    setPickupDays,
    setErrors,
    setDevSellInitialStep,
    setDevSellBandErrorOverride,
    setDevImageValidationPreview,
  } = context;

  const result = getSellDevToolboxApplyResult(scenarioId);

  if (!result) {
    if (isSellerScenario(lastScenarioRef.current)) {
      const empty = getSellDevToolboxApplyResult("seller-listing.form-empty");
      if (empty) {
        setFormData((prev) => ({
          ...prev,
          ...empty.formData,
          category: (empty.formData.category || "") as ItemCategory,
          images: [],
          pickupAvailability: [],
        }));
        setUploadedImages([]);
        setPickupDays([]);
        setErrors({});
      }
    }
    lastScenarioRef.current = null;
    setDevSellInitialStep(1);
    setDevSellBandErrorOverride(null);
    setDevImageValidationPreview(null);
    return;
  }

  lastScenarioRef.current = scenarioId;
  setListingMode("single");
  setFormData((prev) => ({
    ...prev,
    ...result.formData,
    category: (result.formData.category || "") as ItemCategory,
    images: result.uploadedImages,
    pickupAvailability: prev.pickupAvailability,
    pickupDays: result.pickupDays,
  }));
  setUploadedImages(result.uploadedImages);
  setPickupDays(result.pickupDays);
  setErrors({});
  setDevSellInitialStep(result.initialStep);
  setDevSellBandErrorOverride(result.bandError);
  setDevImageValidationPreview(result.imageValidationPreview);
  applySellPhotoCoachDismissState(result.photoCoachDismissed);

  if (
    result.bandSnapshot &&
    result.formData.title &&
    result.formData.category
  ) {
    primeSellPriceBandCache(
      result.formData.title,
      result.formData.category,
      result.formData.subcategory,
      result.bandSnapshot,
    );
  }
}

export function subscribeSellDevToolboxAdapter(
  context: SellDevToolboxContext,
): () => void {
  if (!isDevToolboxEnabled()) return () => {};

  return bindDevToolboxScenarioIdListener((scenarioId) =>
    applyDevToolboxScenario(scenarioId, context),
  );
}

export function bindSellerPhotoCoachDevToolbox(
  onChange: () => void,
): () => void {
  return bindDevToolboxScenarioListener(onChange);
}
